import { type VFC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { toSafeInt } from "widgets/utils";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import VariantGenomeView from "./VariantGenomeView.js";
import VariantSummaryView from "./VariantSummaryView.js";
import VariantsGenericVariantsView from "./VariantsGenericVariantsView.js";
import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { RESET_GENOME, RESET_MUTATION } from "@/redux/action-types.ts";
import { type IMutationState, shouldLoadGenMutation } from "@/redux/modules/mutation.ts";
import { type IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE } from "@/settings/settings.js";

/**
 * The VariantPage smart component is called from the route corresponding to variant/:variantId.
 * The children prop corresponds to the smart component injected from the nested routes (e.g. variant/:variantId/summary)
 * The VariantPage will take care of filling up the basic variant data and the nested child pages may request additional
 * data that is needed to show the nested page (such as transcript and protein child collections for the summary page)
 */
const VariantDetailPage: VFC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { variantId } = useParams<"variantId">();
  const { mutation, isLoading } = useSelector<IAppState, IMutationState>((state) => state.mutation);

  // The shouldLoadGenMutation() is dependant of refGenomeId, that's why we need to reload if it changed.
  const refGenomeId = useSelector<IAppState>((state) => state.auth.session?.params?.referenceGenome);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadGenMutation, variantId);

  // If the refGenomeId changes, the gene mutation should be reloaded accordingly
  useEffect(() => {
    void load();
    // cleanup of the mutation summary store
    return () => {
      dispatch({ type: RESET_MUTATION });
    };
  }, [load, refGenomeId]);

  const getNavItems = (): IRouteNavItemProps[] => {
    const relatedMutationsDec = {
      disabled: isLoading || (mutation && !mutation.hasRelatedMutations),
      tooltip:
        mutation && !mutation.hasRelatedMutations ? "There are no other variants related to this variant" : undefined,
    };
    const affectedGenesDec = {
      disabled: isLoading || (mutation && !mutation.hasAffectedGenes),
      tooltip: mutation && !mutation.hasAffectedGenes ? "There are no genes affected by this variant" : undefined,
    };
    const affectedProteinsDec = {
      disabled: isLoading || (mutation && !mutation.hasAffectedProteins),
      tooltip: mutation && !mutation.hasAffectedProteins ? "There are no proteins affected by this variant" : undefined,
    };
    const genomeViewDec = {
      disabled: isLoading,
    };

    return [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <VariantSummaryView />,
        default: true,
        isLoading,
      },
      {
        label: "Related Variants",
        disabled: relatedMutationsDec.disabled,
        disabledTooltip: relatedMutationsDec.tooltip,
        route: "variants",
        pageComponent: (
          <VariantsGenericVariantsView
            partialVariantsTableSpec={{
              idType: DATATABLE_ID_TYPE.ENTITY,
              queryType: ENTITY_TYPE.MUTATION,
              resultType: ENTITY_TYPE.VARIANT,
            }}
            tableTitle={"Related Variants"}
          />
        ),
        isLoading,
      },
      {
        label: "Affected Genes",
        disabled: affectedGenesDec.disabled,
        disabledTooltip: affectedGenesDec.tooltip,
        route: "genes",
        pageComponent: (
          <VariantsGenericVariantsView
            partialVariantsTableSpec={{
              idType: DATATABLE_ID_TYPE.ENTITY,
              queryType: ENTITY_TYPE.MUTATION,
              resultType: ENTITY_TYPE.GENE_MUTATION,
            }}
            tableTitle={"Affected Genes"}
          />
        ),
        isLoading,
      },
      {
        label: "Affected Proteins",
        disabled: affectedProteinsDec.disabled,
        disabledTooltip: affectedProteinsDec.tooltip,
        route: "proteins",
        pageComponent: (
          <VariantsGenericVariantsView
            partialVariantsTableSpec={{
              idType: DATATABLE_ID_TYPE.ENTITY,
              queryType: ENTITY_TYPE.MUTATION,
              resultType: ENTITY_TYPE.PROTEIN_MUTATION,
            }}
            tableTitle={"Affected Proteins"}
          />
        ),
        isLoading,
      },
      {
        label: "Genome View",
        disabled: genomeViewDec.disabled,
        route: "genome",
        pageComponent: <VariantGenomeView />,
        isLoading,
      },
    ];
  };

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.MUTATION}
      entityId={toSafeInt(variantId)}
      getDisplayName={() => mutation?.refVariant?.name}
      sidebarNavItems={getNavItems()}
      isLoading={isLoading}
    />
  );
};

export default VariantDetailPage;
